import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import {
  Calendar, MapPin, Users, Plus, X, Music, Mic2, Radio, Star, Disc3, Clock,
  ImagePlus, DollarSign, ListMusic, ExternalLink, Filter,
} from "lucide-react";

import { toast } from "sonner";

const TYPE_CONFIG = {
  concert: { icon: Music, label: "Concert", color: "from-[#FF6B35] to-[#FF8FA3]" },
  live: { icon: Radio, label: "Live", color: "from-[#7000FF] to-[#FF6B35]" },
  meetup: { icon: Users, label: "Meetup", color: "from-emerald-500 to-teal-400" },
  release: { icon: Disc3, label: "Sortie", color: "from-[#FF8FA3] to-[#7000FF]" },
  other: { icon: Star, label: "Autre", color: "from-[#6E6680] to-[#AFA8BA]" },
};

export default function EventsPage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState("upcoming");
  const [filterType, setFilterType] = useState(null);
  const [events, setEvents] = useState([]);
  const [myEvents, setMyEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const coverRef = useRef(null);

  // Auto-open create modal when ?create=1 in URL (and user is artist)
  useEffect(() => {
    if (searchParams.get("create") === "1" && user?.role === "artist") {
      setCreating(true);
    }
  }, [searchParams, user]);
  const [newEvent, setNewEvent] = useState({
    title: "", description: "", event_type: "concert", date: "", time: "",
    location: "", address: "", ticket_price: "", ticket_url: "", max_attendees: "",
    setlist: "",
  });
  const [createLoading, setCreateLoading] = useState(false);

  const isArtist = user?.role === "artist";

  const fetchEvents = useCallback(async () => {
    try {
      const params = { limit: 30, upcoming: true };
      if (filterType) params.event_type = filterType;
      const res = await api.get("/events", { params });
      setEvents(res.data.events);
    } catch (err) { console.error(err); }
  }, [api, filterType]);

  const fetchMyEvents = useCallback(async () => {
    try {
      const res = await api.get("/events/my");
      setMyEvents(res.data.events);
    } catch (err) { console.error(err); }
  }, [api]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchEvents(), fetchMyEvents()]).finally(() => setLoading(false));
  }, [fetchEvents, fetchMyEvents]);

  const handleAttend = async (eventId) => {
    try {
      const res = await api.post(`/events/${eventId}/attend`);
      const update = (list) =>
        list.map((e) =>
          e.id === eventId
            ? { ...e, is_attending: res.data.attending, attendee_count: res.data.attendee_count }
            : e
        );
      setEvents(update);
      setMyEvents(update);
      if (res.data.attending) fetchMyEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleCoverSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setCoverPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleCreate = async () => {
    if (!newEvent.title.trim() || !newEvent.date) return;
    setCreateLoading(true);
    try {
      let cover_url = null;
      if (coverFile) {
        const formData = new FormData();
        formData.append("file", coverFile);
        const uploadRes = await api.post("/uploads", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        cover_url = uploadRes.data.url;
      }

      const setlistArr = newEvent.setlist
        ? newEvent.setlist.split("\n").map((s) => s.trim()).filter(Boolean)
        : [];

      const payload = {
        title: newEvent.title,
        description: newEvent.description,
        event_type: newEvent.event_type,
        date: newEvent.date,
        time: newEvent.time,
        location: newEvent.location,
        address: newEvent.address,
        ticket_price: newEvent.ticket_price ? parseFloat(newEvent.ticket_price) : null,
        ticket_url: newEvent.ticket_url,
        max_attendees: newEvent.max_attendees ? parseInt(newEvent.max_attendees) : null,
        cover_url,
        setlist: setlistArr,
      };
      await api.post("/events", payload);
      setCreating(false);
      setNewEvent({
        title: "", description: "", event_type: "concert", date: "", time: "",
        location: "", address: "", ticket_price: "", ticket_url: "", max_attendees: "",
        setlist: "",
      });
      setCoverFile(null);
      setCoverPreview(null);
      fetchEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
    setCreateLoading(false);
  };

  const displayedEvents = tab === "my" ? myEvents : events;

  return (
    <div className="py-6 space-y-6" data-testid="events-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl text-foreground mb-1">Evenements</h1>
          <p className="text-sm text-muted-foreground">Concerts, lives et rencontres</p>
        </div>
        {isArtist && (
          <button
            onClick={() => setCreating(true)}
            className="bg-[#FF6B35] text-white font-bold px-4 py-2 rounded-full text-sm hover:scale-105 active:scale-95 transition-transform flex items-center gap-1.5"
            data-testid="create-event-btn"
          >
            <Plus className="w-4 h-4" />
            Creer
          </button>
        )}
      </div>

      {/* Type filters */}
      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
        <button
          onClick={() => setFilterType(null)}
          className={`flex items-center gap-1 text-[11px] px-3 py-1.5 rounded-full border transition-all shrink-0 ${
            !filterType ? "bg-[#FF6B35]/10 border-[#FF6B35]/40 text-[#FF6B35]" : "bg-card border-border text-muted-foreground"
          }`}
          data-testid="filter-all"
        >
          <Filter className="w-3 h-3" /> Tous
        </button>
        {Object.entries(TYPE_CONFIG).map(([key, cfg]) => {
          const Icon = cfg.icon;
          return (
            <button
              key={key}
              onClick={() => setFilterType(filterType === key ? null : key)}
              className={`flex items-center gap-1 text-[11px] px-3 py-1.5 rounded-full border transition-all shrink-0 ${
                filterType === key ? "bg-[#FF6B35]/10 border-[#FF6B35]/40 text-[#FF6B35]" : "bg-card border-border text-muted-foreground"
              }`}
              data-testid={`filter-${key}`}
            >
              <Icon className="w-3 h-3" /> {cfg.label}
            </button>
          );
        })}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-card rounded-full p-1 border border-border">
        <button
          onClick={() => setTab("upcoming")}
          className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
            tab === "upcoming" ? "bg-[#FF6B35] text-white" : "text-muted-foreground"
          }`}
          data-testid="tab-upcoming-events"
        >
          <Calendar className="w-4 h-4" />
          A venir
        </button>
        <button
          onClick={() => setTab("my")}
          className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
            tab === "my" ? "bg-[#FF6B35] text-white" : "text-muted-foreground"
          }`}
          data-testid="tab-my-events"
        >
          <Star className="w-4 h-4" />
          Mes events ({myEvents.length})
        </button>
      </div>

      {/* Events list */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-card rounded-3xl h-32 animate-pulse" />
          ))}
        </div>
      ) : displayedEvents.length === 0 ? (
        <div className="text-center py-16">
          <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">
            {tab === "my" ? "Tu n'as aucun evenement prevu" : "Aucun evenement a venir"}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {displayedEvents.map((e) => {
            const config = TYPE_CONFIG[e.event_type] || TYPE_CONFIG.other;
            const TypeIcon = config.icon;
            return (
              <div
                key={e.id}
                className="bg-card border border-border rounded-3xl overflow-hidden hover:border-[#FF6B35]/30 transition-colors duration-300 cursor-pointer shadow-sm"
                onClick={() => navigate(`/events/${e.id}`)}
                data-testid={`event-card-${e.id}`}
              >
                {e.cover_url ? (
                  <div className="h-32 relative">
                    <img src={e.cover_url} alt="" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-3 left-4 right-4 flex items-end justify-between">
                      <div>
                        <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-gradient-to-r ${config.color} text-white`}>
                          <TypeIcon className="w-3 h-3" />
                          {config.label}
                        </span>
                        <h3 className="font-heading font-bold text-white mt-1 text-lg">{e.title}</h3>
                      </div>
                      {e.ticket_price != null && (
                        <span className="bg-[#FF6B35] text-white text-xs font-bold px-2 py-1 rounded-full shrink-0">
                          {e.ticket_price === 0 ? "Gratuit" : `${e.ticket_price}EUR`}
                        </span>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className={`h-1.5 bg-gradient-to-r ${config.color}`} />
                )}
                <div className="p-5">
                  <div className="flex items-start gap-4">
                    {/* Date block */}
                    <div className="bg-muted rounded-2xl p-3 text-center shrink-0 border border-border min-w-[60px]">
                      <p className="text-[10px] text-muted-foreground uppercase">
                        {new Date(e.date).toLocaleDateString("fr-FR", { month: "short" })}
                      </p>
                      <p className="font-heading font-extrabold text-2xl text-foreground -mt-0.5">
                        {new Date(e.date).getDate()}
                      </p>
                      {e.time && (
                        <p className="text-[10px] text-[#FF6B35] font-semibold mt-0.5">{e.time}</p>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      {!e.cover_url && (
                        <>
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-gradient-to-r ${config.color} text-white`}>
                              <TypeIcon className="w-3 h-3" />
                              {config.label}
                            </span>
                            {e.ticket_price != null && (
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#FF6B35]/10 text-[#FF6B35] border border-[#FF6B35]/20">
                                {e.ticket_price === 0 ? "Gratuit" : `${e.ticket_price}EUR`}
                              </span>
                            )}
                          </div>
                          <h3 className="font-heading font-bold text-foreground truncate">{e.title}</h3>
                        </>
                      )}
                      <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground flex-wrap">
                        <span className="flex items-center gap-1">
                          <Mic2 className="w-3 h-3" />
                          {e.artist_name}
                        </span>
                        {e.location && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {e.location}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {e.attendee_count}{e.max_attendees ? `/${e.max_attendees}` : ""}
                        </span>
                      </div>
                    </div>

                    <button
                      onClick={(ev) => { ev.stopPropagation(); handleAttend(e.id); }}
                      className={`text-xs font-semibold px-4 py-1.5 rounded-full transition-all duration-200 shrink-0 ${
                        e.is_attending
                          ? "bg-muted text-muted-foreground border border-border"
                          : "bg-[#FF6B35] text-white shadow-[0_0_15px_rgba(255,107,53,0.3)] hover:scale-105 active:scale-95"
                      }`}
                      data-testid={`attend-btn-${e.id}`}
                    >
                      {e.is_attending ? "Inscrit" : "Participer"}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create event modal */}
      {creating && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4" data-testid="create-event-modal">
          <div className="bg-card border border-border rounded-3xl p-6 w-full max-w-lg space-y-4 max-h-[85vh] overflow-y-auto shadow-xl">
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-bold text-lg text-foreground">Nouvel evenement</h3>
              <button onClick={() => setCreating(false)} className="text-muted-foreground hover:text-foreground">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Cover image */}
            {coverPreview ? (
              <div className="relative">
                <img src={coverPreview} alt="" className="w-full h-40 object-cover rounded-2xl" />
                <button
                  onClick={() => { setCoverFile(null); setCoverPreview(null); }}
                  className="absolute top-2 right-2 bg-black/60 rounded-full p-1"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => coverRef.current?.click()}
                className="w-full h-28 rounded-2xl border-2 border-dashed border-border flex flex-col items-center justify-center gap-1 hover:border-[#FF6B35]/40 transition-colors"
                data-testid="event-cover-btn"
              >
                <ImagePlus className="w-5 h-5 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Image de couverture</span>
              </button>
            )}
            <input ref={coverRef} type="file" accept="image/*" onChange={handleCoverSelect} className="hidden" />

            <Input
              placeholder="Titre de l'evenement *"
              value={newEvent.title}
              onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
              className="bg-background border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] h-11"
              data-testid="event-title-input"
            />

            <textarea
              placeholder="Description (optionnel)"
              value={newEvent.description}
              onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
              className="w-full bg-background border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35] outline-none resize-none h-20"
              data-testid="event-description-input"
            />

            {/* Type */}
            <div>
              <p className="text-xs text-muted-foreground mb-2">Type</p>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(TYPE_CONFIG).map(([key, cfg]) => {
                  const Icon = cfg.icon;
                  return (
                    <button
                      key={key}
                      onClick={() => setNewEvent({ ...newEvent, event_type: key })}
                      className={`flex items-center gap-1 text-[10px] px-3 py-1.5 rounded-full border transition-all ${
                        newEvent.event_type === key
                          ? "bg-[#FF6B35]/10 border-[#FF6B35]/40 text-[#FF6B35]"
                          : "bg-background border-border text-muted-foreground"
                      }`}
                    >
                      <Icon className="w-3 h-3" />
                      {cfg.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Date / Time / Places */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Date *</p>
                <input
                  type="date"
                  value={newEvent.date}
                  onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                  className="w-full bg-background border border-border rounded-xl px-3 py-2 text-sm text-foreground focus:border-[#FF6B35] outline-none"
                  data-testid="event-date-input"
                />
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Heure</p>
                <input
                  type="time"
                  value={newEvent.time}
                  onChange={(e) => setNewEvent({ ...newEvent, time: e.target.value })}
                  className="w-full bg-background border border-border rounded-xl px-3 py-2 text-sm text-foreground focus:border-[#FF6B35] outline-none"
                  data-testid="event-time-input"
                />
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Places max</p>
                <Input
                  type="number"
                  placeholder="Illimite"
                  value={newEvent.max_attendees}
                  onChange={(e) => setNewEvent({ ...newEvent, max_attendees: e.target.value })}
                  className="bg-background border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] h-10"
                  data-testid="event-max-input"
                />
              </div>
            </div>

            {/* Location */}
            <Input
              placeholder="Lieu / Salle"
              value={newEvent.location}
              onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
              className="bg-background border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] h-11"
              data-testid="event-location-input"
            />
            <Input
              placeholder="Adresse complete"
              value={newEvent.address}
              onChange={(e) => setNewEvent({ ...newEvent, address: e.target.value })}
              className="bg-background border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] h-11"
              data-testid="event-address-input"
            />

            {/* Pricing */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1"><DollarSign className="w-3 h-3" /> Prix (EUR, 0=gratuit)</p>
                <Input
                  type="number"
                  placeholder="Gratuit"
                  value={newEvent.ticket_price}
                  onChange={(e) => setNewEvent({ ...newEvent, ticket_price: e.target.value })}
                  className="bg-background border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] h-10"
                  data-testid="event-price-input"
                />
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Lien billetterie</p>
                <Input
                  placeholder="https://..."
                  value={newEvent.ticket_url}
                  onChange={(e) => setNewEvent({ ...newEvent, ticket_url: e.target.value })}
                  className="bg-background border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] h-10"
                  data-testid="event-ticket-url-input"
                />
              </div>
            </div>

            {/* Setlist */}
            <div>
              <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                <ListMusic className="w-3 h-3" /> Setlist (un titre par ligne)
              </p>
              <textarea
                placeholder="Morceau 1&#10;Morceau 2&#10;Morceau 3"
                value={newEvent.setlist}
                onChange={(e) => setNewEvent({ ...newEvent, setlist: e.target.value })}
                className="w-full bg-background border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35] outline-none resize-none h-20"
                data-testid="event-setlist-input"
              />
            </div>

            <button
              onClick={handleCreate}
              disabled={createLoading || !newEvent.title.trim() || !newEvent.date}
              className="w-full bg-[#FF6B35] text-white font-bold py-3 rounded-full text-sm hover:scale-[1.02] active:scale-[0.98] transition-transform disabled:opacity-50"
              data-testid="event-create-submit"
            >
              {createLoading ? "..." : "Creer l'evenement"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
